import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Alert, Box, Grid, Heading, Segmented, Spinner, Stack, Text } from '@ui'
import { APP_NAME } from '../brand.js'
import ProfileChart from '../components/ProfileChart.jsx'
import { PhotoGrid, PhotoUrlContext, PhotoViewer } from '../components/Photos.jsx'
import Splits from '../components/Splits.jsx'
import SharedPhotos from './SharedPhotos.jsx'
import WalkMap from '../components/WalkMap.jsx'
import { COMPARISON_SOURCES, formatDate, formatDistance, formatDuration, formatNumber, formatPace, formatTime } from '../format.js'
import { placePhotos } from '../photoPins.js'
import { useAuth } from '../auth.jsx'
import { useUnit } from '../units.jsx'
import { Climb, Stat, elevationText, recordedOn } from './Walk.jsx'

// A walk shared by a public link (/s/<token>; cmd/server/shares.go): a read-only copy of the walk
// page for anyone with the link, no sign-in. Everything comes from GET /api/share/:token, which
// has already left out what the owner chose to hide. A link that has expired, been stopped or
// never existed gets a page saying which. A link to the Photos page (kind 'photos') is drawn by
// SharedPhotos.jsx from the same reply.

const when = (iso) => `${formatDate(iso)}, ${formatTime(iso)}`
const appOf = (source) => (source === 'mmw_import' ? 'MapMyWalk' : COMPARISON_SOURCES[source] ?? APP_NAME)

function Gone({ title, children }) {
  return (
    <Box className="legal">
      <Heading size={3}>{title}</Heading>
      <p>{children}</p>
    </Box>
  )
}

export default function SharedWalk() {
  const { token } = useParams()
  const [unit, setUnit] = useUnit()
  const { status } = useAuth()
  const [reply, setReply] = useState(null) // { status, … } from the server
  const [error, setError] = useState(null)
  const [hover, setHover] = useState(null)
  const [photoIndex, setPhotoIndex] = useState(null)

  // Not for search engines (the server also sends X-Robots-Tag).
  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex, nofollow'
    document.head.appendChild(meta)
    return () => meta.remove()
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    fetch(`/api/share/${encodeURIComponent(token)}`, { signal: controller.signal })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}))
        if (res.status === 429) throw new Error('Too many requests just now. Try again in a minute.')
        if (!body.status) throw new Error(body.error ?? `HTTP ${res.status}`)
        setReply(body)
      })
      .catch((err) => { if (err.name !== 'AbortError') setError(err.message) })
    return () => controller.abort()
  }, [token])

  // An ended link opened signed in: the server's page title (written before it could know who's
  // looking, since the sign-in cookie doesn't come with a link opened from another app) says the
  // link has ended, so put the walk's title back.
  useEffect(() => {
    if (!reply?.ended || reply.kind === 'photos') return
    const name = reply.walk.name || `Walk on ${formatDate(`${reply.walk.date}T12:00:00Z`)}`
    document.title = `${name} · ${APP_NAME}`
  }, [reply])

  const placed = useMemo(() => placePhotos(reply?.photos ?? [], reply?.track), [reply])
  const photoUrl = useMemo(() => (id, size) => `/api/share/${encodeURIComponent(token)}/photos/${id}/${size}`, [token])

  if (error) return <Alert variant="danger">Couldn't load this walk: {error}</Alert>
  if (!reply) return <Spinner />
  const what = reply.kind === 'photos' ? 'photos' : 'walk'
  if (reply.status === 'expired') {
    return (
      <Gone title="This link has expired">
        The {what === 'photos' ? 'photos were' : 'walk was'} shared until {when(reply.at)}, and the
        link no longer opens {what === 'photos' ? 'them' : 'it'}. If you still want to see{' '}
        {what === 'photos' ? 'them' : 'it'}, ask the person who sent you the link for a new one.
      </Gone>
    )
  }
  if (reply.status === 'stopped') {
    return (
      <Gone title={what === 'photos' ? 'These photos are no longer shared' : 'This walk is no longer shared'}>
        The person who shared {what === 'photos' ? 'them' : 'it'} stopped this link on {when(reply.at)}.
        If you still want to see the {what}, ask them for a new link.
      </Gone>
    )
  }
  if (reply.status !== 'ok') {
    return (
      <Gone title="This link doesn't open anything">
        There's nothing shared at this address. Check that the whole link was copied, or ask the
        person who sent it for a new one.
      </Gone>
    )
  }
  if (reply.kind === 'photos') return <SharedPhotos reply={reply} token={token} />

  const { walk, track, photos, share } = reply
  const hidden = share.hide_details
  const day = formatDate(`${walk.date}T12:00:00Z`)
  const title = walk.name || `Walk on ${day}`
  const elevation = elevationText(track.elevation)
  const subtitle = hidden
    ? `${walk.name ? `${day} · ` : ''}Recorded with ${appOf(walk.source)}`
    : `${formatDate(walk.start_time)}, ${formatTime(walk.start_time)}–${formatTime(walk.end_time)} · ${recordedOn(walk)}`

  return (
    <PhotoUrlContext.Provider value={photoUrl}>
      <Stack wrap align="center" gap={2} mb={1}>
        <Heading size={3} mb={0} me="auto">{title}</Heading>
        {status !== 'signed-in' && ( // signed in, the navbar has one
          <Segmented
            name="shared-unit" size="sm" value={unit} onChange={setUnit} aria-label="Distance unit"
            options={[{ value: 'mi', label: 'mi' }, { value: 'km', label: 'km' }]}
          />
        )}
      </Stack>
      <Text as="p" muted mb={3}>{subtitle}</Text>
      {reply.ended && (
        <Alert variant="info" compact>
          {reply.ended.status === 'expired' ? `This link expired on ${when(reply.ended.at)}` : `This link was stopped on ${when(reply.ended.at)}`}.
          You can still see the walk because you're signed in; anyone else is told the link no longer works.
        </Alert>
      )}
      {!hidden && walk.notes && <Box as="p" mb={3} style={{ whiteSpace: 'pre-wrap' }}>{walk.notes}</Box>}

      <Grid gap={3} mb={4}>
        <Stat label="Distance" value={formatDistance(walk.distance_meters, unit)} />
        <Stat label="Moving time" value={formatDuration(walk.moving_duration_ms)} />
        <Stat label="Pace" value={`${formatPace(walk.moving_duration_ms, walk.distance_meters, unit)} /${unit}`} />
        <Climb walk={walk} elevation={track.elevation} />
        <Stat label="Steps" value={formatNumber(walk.steps)} />
        {walk.calories_kcal != null && <Stat label="Calories" value={formatNumber(walk.calories_kcal)} />}
      </Grid>

      {track.points.length > 0 ? (
        <>
          <WalkMap points={track.points} hover={hover} pins={placed.pins} onOpenPhoto={setPhotoIndex} tiles="osm" trimmed={hidden} />
          {hidden && (
            <Text as="p" muted text="small" mt={1} mb={0}>
              The first and last {share.trim_meters} m of the route aren't shown; the figures above are the whole walk's.
            </Text>
          )}
        </>
      ) : (
        <Text as="p" muted>{track.raw_point_count === 0 ? 'This walk has no GPS route.' : 'The route is too short to show with its ends hidden.'}</Text>
      )}

      {photos.length > 0 && (
        <>
          <Heading level={2} size={5} mt={4}>
            {photos.some((p) => p.media_type === 'video') ? 'Photos and videos' : 'Photos'}{' '}
            <Text muted weight="normal">({photos.length})</Text>
          </Heading>
          <PhotoGrid photos={photos} onOpen={setPhotoIndex} />
          <PhotoViewer photos={photos} index={photoIndex} onIndex={setPhotoIndex} walkStart={walk.start_time} readOnly />
        </>
      )}

      {track.points.length > 1 && (
        <>
          <Heading level={2} size={5} mt={4}>Elevation and pace</Heading>
          <ProfileChart
            points={track.points} unit={unit} hover={hover} onHover={setHover}
            photos={placed.pins.filter((p) => p.d != null)} onOpenPhoto={setPhotoIndex} walkStart={walk.start_time}
            elevationLabel={elevation.label} elevationLabelShort={elevation.short}
          />
          <Heading level={2} size={5} mt={4}>Splits</Heading>
          <Splits points={track.points} unit={unit} />
          {elevation.note && <Text as="p" muted text="small">{elevation.note}</Text>}
        </>
      )}

      <Text as="p" muted text="small" mt={4} mb={0}>
        Shared from {APP_NAME}. {share.expires_at && !reply.ended ? `This link works until ${when(share.expires_at)}.` : ''}
      </Text>
    </PhotoUrlContext.Provider>
  )
}
